import { Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';

import { ApplicationConfiguration } from '@core/configuration';
import { securityLog } from '../helpers';
import { AccountContextService } from './account-context.service';
import { MsalAccountRepository } from '../repositories';
import { TenantClaimResolverService } from './tenant-claim-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class AuthAccountService {
    constructor(
        private applicationConfig: ApplicationConfiguration,
        private accountContext: AccountContextService,
        private accountRepository: MsalAccountRepository,
        private tenantClaimResolver: TenantClaimResolverService,
    ) { }

    public enableAccountStorageEvents(): void {
        this.accountRepository.enableAccountStorageEvents();
    }

    public getActiveAccount(): AccountInfo | null {
        return this.accountRepository.getActiveAccount();
    }

    public getAllAccounts(): AccountInfo[] {
        return this.accountRepository.getAllAccounts();
    }

    public setActiveAccount(account: AccountInfo | null): void {
        this.accountRepository.setActiveAccount(account);
    }

    public hasActiveAccount(): boolean {
        return this.accountRepository.hasActiveAccount();
    }

    public resolveCachedAccount(): AccountInfo | undefined {
        return this.accountContext.resolveBestAccount(this.getActiveAccount(), this.getAllAccounts());
    }

    public hydrateTenantFromCachedAccount(): AccountInfo | undefined {
        const cachedAccount = this.resolveCachedAccount();
        if (!cachedAccount) {
            return undefined;
        }

        this.setActiveAccount(cachedAccount);
        this.accountContext.updateAuthContextForAccount(cachedAccount);

        const tenant = this.tenantClaimResolver.resolveTenantFromClaims(cachedAccount.idTokenClaims as Record<string, unknown> | undefined);
        if (!tenant) {
            securityLog.warn('AuthAccountService: Unable to resolve tenant from cached account claims');
            return cachedAccount;
        }

        securityLog.log(`AuthAccountService: Resolved tenant from cached account claims: ${tenant}`);
        this.applicationConfig.tenant = tenant;
        return cachedAccount;
    }

    public hasActiveTenantClaim(): boolean {
        const activeAccount = this.getActiveAccount();
        if (!activeAccount) {
            return false;
        }

        const claims = activeAccount.idTokenClaims as Record<string, unknown> | undefined;
        return !!this.tenantClaimResolver.resolveTenantFromClaims(claims);
    }
}